import {cn, IconBox, Typography} from '@sollapay/ui';
import {DirectionalIcon} from '@sollapay/ui/components';

import type {IconName} from '@sollapay/ui';

export interface KpiCardProps {
  icon: IconName;
  label: string;
  value: string;
  change: string | null;
  changeLabel: string;
  direction?: 'increase' | 'decrease';
  noChangeLabel?: string;
  isPositive?: boolean;
  onClick?: () => void;
  navigateLabel?: string;
  /** Marks the card as the active filter pill — shown as a persistent bottom accent bar, distinct from the hover tint. */
  isSelected?: boolean;
  /** Overrides the value's text color, e.g. to keep a warning tone regardless of selection. */
  valueClassName?: string;
}

export function KpiCard({
  icon,
  label,
  value,
  change,
  changeLabel,
  direction,
  noChangeLabel,
  isPositive,
  onClick,
  navigateLabel,
  isSelected,
  valueClassName
}: KpiCardProps) {
  const isNegative = direction === 'decrease';
  const isChangePositive = direction === 'increase' || (isPositive === true && change !== null);
  const isClickable = onClick !== undefined;

  return (
    <div
      className={cn(
        'relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-border-subtle bg-background-surface p-5 shadow-sm',
        isClickable && 'group cursor-pointer transition-colors hover:bg-background-secondary'
      )}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={isClickable && isSelected !== undefined ? isSelected : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-3 flex-1 min-w-0 items-start">
          <Typography size="sm" weight="medium" color="tertiary" className="w-full">
            {label}
          </Typography>
          <div className="flex flex-col gap-2 items-start w-full">
            <Typography weight="semibold" className={cn('text-2xl', valueClassName)}>
              {value}
            </Typography>
            {change !== null ? (
              <div className="flex items-center gap-1">
                <Typography
                  dir="ltr"
                  size="xs"
                  weight="medium"
                  className={cn(
                    isChangePositive
                      ? 'text-fg-credit'
                      : isNegative
                        ? 'text-destructive'
                        : 'text-fg-tertiary'
                  )}
                >
                  {change}
                </Typography>
                <Typography size="xs" color="tertiary">
                  {changeLabel}
                </Typography>
              </div>
            ) : noChangeLabel ? (
              <Typography size="xs" color="tertiary">
                {noChangeLabel}
              </Typography>
            ) : null}
          </div>
        </div>
        <IconBox icon={icon} shadow iconColor="secondary" />
      </div>
      {isSelected ? (
        <div className="absolute inset-x-0 bottom-0 h-0.5 rounded-b-2xl bg-brand-primary" />
      ) : null}
      {isClickable && navigateLabel ? (
        <div className="absolute bottom-3 end-3 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <Typography size="xs" weight="medium" className="text-brand-primary">
            {navigateLabel}
          </Typography>
          <DirectionalIcon name="chevron-forward" className="size-3.5 text-brand-primary" />
        </div>
      ) : null}
    </div>
  );
}
